import { type CSSProperties } from 'react';
import { Alert, Space, Tag, Typography } from 'antd';
import { canMutateSharedInfra } from '../utils/permissions';
import { tokenManager } from '../utils/tokenManager';
import { useViewScope, getViewScopeLabel } from '../utils/viewScope';

type NoticeKind = 'members' | 'tiers' | 'shared-infra' | 'delivery' | 'logs' | 'accounting';

interface Props {
  kind: NoticeKind;
  style?: CSSProperties;
}

const copy: Record<NoticeKind, string> = {
  members: '會員資料按品牌隔離；同名帳號可存在於不同品牌，但同品牌內不可重複。新增會員時需在表單中指定目標品牌。',
  tiers: '等級方案按品牌隔離；會員只能指派同品牌的等級。',
  'shared-infra':
    '節點、VLESS 與優選 IP 是共享運維資源；品牌管理員只看可用範圍，全品牌管理員可管理全部。',
  delivery: '版本與 OTA 依品牌分流；發布前確認品牌、平台與版本號一致。',
  logs: '日誌與稽核按目前檢視範圍查詢；共享運維操作會保留操作紀錄。',
  accounting:
    '帳務資料按目前檢視範圍查詢；用右上角「檢視範圍」切換單一品牌或彙總全部，匯出 CSV 一併遵守品牌權限。',
};

/**
 * 品牌範圍說明（去品牌化 M1 後為純資訊）。
 *
 * 過去這個元件內嵌一個會打 switch-brand + reload 的第二個品牌選擇器
 * （「點功能頁又能選品牌」混淆來源），已在 M1 移除。
 * 現在只顯示目前「檢視範圍」與品牌隔離規則，唯一切換點在右上角 Header。
 */
export const BrandScopeNotice: React.FC<Props> = ({ kind, style }) => {
  const { scope, isAllScope } = useViewScope();
  const isAllBrandAdmin = tokenManager.isAllBrandAdmin();
  const sharedReadonly = kind === 'shared-infra' && !canMutateSharedInfra();

  return (
    <Alert
      showIcon
      type={sharedReadonly ? 'info' : 'success'}
      style={style}
      message={
        <Space size={8} wrap>
          <Typography.Text strong>檢視範圍</Typography.Text>
          <Tag color={isAllScope ? 'gold' : 'blue'}>{getViewScopeLabel(scope)}</Tag>
          {isAllBrandAdmin ? <Tag>全品牌管理員</Tag> : <Tag>品牌管理員</Tag>}
          {sharedReadonly ? <Tag color="orange">共享資源只讀</Tag> : null}
        </Space>
      }
      description={copy[kind]}
    />
  );
};
